import {Selector} from 'notebook/ELNConstants';
import {TableEvaluation, stubbableFunctions} from 'notebook/mediocre/DocumentEvaluationUpdater';
import excess from 'notebook/tables/excess';
import {MultidocTableOpMap} from 'notebook/tables/TableConstants';

import {NoteRuntimeSnapshot} from '../TableStoreTransforms';
import {MultidocFullTableMap, MultidocOutputTableMap} from './Evaluator';
import Resolver from './Resolver';

function _getPrevEvalSnapshot(
  noteRuntimeSnapshots: Map<string, NoteRuntimeSnapshot>
): MultidocOutputTableMap | null {
  const hasPrevEvalSnapshots =
    noteRuntimeSnapshots.size > 0 &&
    [...noteRuntimeSnapshots.values()].every((nrs) => nrs.evaluationSnapshot != null);
  if (!hasPrevEvalSnapshots) {
    return null;
  }

  const prevEvalSnapshot: MultidocOutputTableMap = {};
  noteRuntimeSnapshots.forEach((nrs, docId) => {
    prevEvalSnapshot[docId] = nrs.evaluationSnapshot!;
  });
  return prevEvalSnapshot;
}

/*
 * Evaluate every cell in the tableMap every time you run evaluation
 */
export const runEvaluation = ({
  itemCids,
  fileId,
  fullTableMap,
  selectors,
  noteRuntimeSnapshots,
  tableNameToProvenanceUuidMap,
  documentNameToIds = {},
  multidocTableOp,
}: {
  itemCids: Map<string, string>;
  fileId: string;
  fullTableMap: MultidocFullTableMap;
  selectors: Map<string, Array<Selector>>;
  noteRuntimeSnapshots: Map<string, NoteRuntimeSnapshot>;
  tableNameToProvenanceUuidMap: Map<string, Record<PropertyKey, string | null>>;
  documentNameToIds?: {[documentName: string]: Array<string>};
  multidocTableOp?: MultidocTableOpMap;
}): Map<string, TableEvaluation> => {
  if (window.debug) {
    // oxlint-disable-next-line no-console
    console.info(`[EXCESS] [${fileId}] Running traditional evaluator for persistence`);
  }

  const resolvers = new Map<string, Resolver>();
  noteRuntimeSnapshots.forEach((nrs, docId) => {
    resolvers.set(
      docId,
      stubbableFunctions.makeResolver(nrs, () => {
        const currentTableName = evaluator.env.currentTable!;
        return (
          tableNameToProvenanceUuidMap.get(docId)![currentTableName] ??
          // TODO(BNCH-19669): remove this fallback once provenanceUuid is backfilled for legacy results tables
          currentTableName
        );
      })
    );
  });

  const prevEvalSnapshot = _getPrevEvalSnapshot(noteRuntimeSnapshots);

  const evaluator = new excess.MultiDocSpreadsheetEvaluator({
    fullTableMap,
    selectors,
    parse: excess.parse,
    resolvers,
    primaryFileId: fileId,
    documentNameToIds,
    multidocTableOp,
    prevEvalSnapshot,
  });
  const evaluationSnapshot = evaluator.output();
  const result: Map<string, TableEvaluation> = new Map();
  Object.keys(evaluationSnapshot).forEach((docId) => {
    const {syncResolutions, asyncResolutions, cellsWithUnresolvedResolutionQueries} = resolvers
      .get(docId)!
      .getResolutions();
    result.set(docId, {
      cid: itemCids.get(docId)!,
      evaluationSnapshot: evaluationSnapshot[docId],
      syncResolutions,
      asyncResolutions,
      cellsWithUnresolvedResolutionQueries,
    });
  });
  return result;
};
